import { textLength, type DesignTextLayer, type DesignTextStyle } from '@pz/scene';
import {
  chipRow,
  el,
  field,
  hexOf,
  note,
  sameHex,
  textBtn,
  type DesignerCtx,
} from './panels.js';
import { openPopover } from './overlay.js';
import { fillText } from './texts.js';
import { canAddLayer, freshTextLayer, textLines, type LayerPatch } from './state.js';
import { run, canDelete, layerLabel } from './commands.js';

/**
 * The Tekst panel: add a text box, and edit the words already in the design.
 *
 * Adding follows the reference editor exactly — a primary button opens a small
 * popover ON the canvas ("Upiši tekst", Otkaži / Dodaj) and the text appears
 * centred and selected. It is a better first step than dropping "Tvoj tekst"
 * on the artboard and hoping the shopper notices they have to retype it.
 *
 * Everything about how the words LOOK lives in the contextual toolbar (font,
 * size, colour, bold, italic, align) and in Efekti (shadow, stroke, spacing,
 * curve, orientation). What stays here is what the reference has no answer for
 * at all: the style chips, a fill + outline + shadow chosen together in looks
 * the shop approved, which is the difference between shopper text that reads
 * as designed and shopper text that reads as typed.
 */

function styleMatches(layer: DesignTextLayer, style: DesignTextStyle): boolean {
  if (!sameHex(layer.color, style.color)) return false;
  const mine = layer.outline;
  const theirs = style.outline;
  if (Boolean(mine) !== Boolean(theirs)) return false;
  if (mine && theirs && (!sameHex(mine.color, theirs.color) || Math.abs(mine.width - theirs.width) > 0.005)) return false;
  return Boolean(layer.shadow) === Boolean(style.shadow);
}

export function renderTextPanel(body: HTMLElement, ctx: DesignerCtx): () => void {
  const cfg = ctx.config;
  const cleanups: Array<() => void> = [];
  const texts = ctx.store.value().layers.filter((layer): layer is DesignTextLayer => layer.kind === 'text');
  const selected = ctx.selected();
  const layer = selected?.kind === 'text' ? selected : undefined;

  // ── Add ─────────────────────────────────────────────────────────────────
  const add = el('button', 'pzd-primary is-wide', ctx.texts.addText);
  add.type = 'button';
  add.addEventListener('click', () => openAddPopover(ctx, add));
  const room = canAddLayer(ctx.store.state(), cfg, 'text');
  add.disabled = !room.ok;
  body.append(add);
  if (!room.ok) {
    body.append(note(fillText(ctx.texts.maxLayers, { n: room.limit ?? cfg.maxLayers }), 'warn'));
  }

  // ── The words already in the design ─────────────────────────────────────
  for (const item of texts) {
    body.append(textField(ctx, item, item.id === layer?.id, cleanups));
  }
  if (!texts.length) body.append(note(ctx.texts.nothingHere));

  // ── Style chips, for the text in hand ───────────────────────────────────
  if (layer) {
    const styleBox = field(ctx.texts.style);
    const row = el('div', 'pzd-grid');
    for (const style of cfg.text.styles) {
      const tile = el('button', `pzd-tile pzd-style-tile${styleMatches(layer, style) ? ' is-on' : ''}`);
      tile.type = 'button';
      tile.setAttribute('aria-label', style.name);
      tile.title = style.name;
      const sample = el('span', 'pzd-style-preview', 'Aa');
      sample.style.color = hexOf(style.color) || '#000';
      sample.style.fontFamily = `${ctx.fonts.cssFamily(layer.fontId)}, sans-serif`;
      if (style.outline) {
        sample.style.setProperty('-webkit-text-stroke', `${Math.max(1, style.outline.width * 22)}px ${hexOf(style.outline.color)}`);
        sample.style.paintOrder = 'stroke fill';
      }
      if (style.shadow) sample.style.textShadow = `2px 2px 3px ${hexOf(style.shadow.color)}`;
      tile.append(sample);
      tile.addEventListener('click', () => applyStyle(ctx, layer, style));
      row.append(tile);
    }
    styleBox.wrap.append(row);
    body.append(styleBox.wrap);

    body.append(chipRow([
      { id: 'duplicate', label: ctx.texts.duplicate, icon: 'copy' },
      { id: 'remove', label: ctx.texts.remove, icon: 'trash', disabled: !canDelete(layer) },
    ], (action) => {
      run(ctx, { op: action === 'duplicate' ? 'duplicate' : 'delete', ids: [layer.id] });
    }));
  }

  return () => {
    for (const stop of cleanups) stop();
  };
}

function applyStyle(ctx: DesignerCtx, layer: DesignTextLayer, style: DesignTextStyle): void {
  const patch: LayerPatch = {
    color: style.color,
    outline: style.outline ?? undefined,
    shadow: ctx.config.text.allowShadow ? style.shadow ?? undefined : undefined,
    styleId: style.id,
  };
  ctx.dispatch({ type: 'update', id: layer.id, patch });
}

/**
 * One text layer as an editable field. Typing is debounced 120 ms and tagged,
 * so a word is one undo step and one rasterisation rather than one of each per
 * letter; the field commits on blur as well, because a shopper who clicks
 * straight onto the canvas has still finished typing.
 */
function textField(
  ctx: DesignerCtx,
  layer: DesignTextLayer,
  active: boolean,
  cleanups: Array<() => void>,
): HTMLElement {
  const cfg = ctx.config;
  const box = field(layerLabel(layer) ?? ctx.texts.layerText);
  const area = el('textarea', `pzd-textarea${active ? ' is-active' : ''}`);
  area.value = layer.text;
  area.placeholder = ctx.texts.textPlaceholder;
  area.maxLength = cfg.text.maxChars;
  area.rows = Math.min(4, Math.max(2, textLines(layer.text).length));
  area.setAttribute('aria-label', layerLabel(layer) ?? ctx.texts.textPlaceholder);
  const counter = el('div', 'pzd-hint');
  const limits = el('div');

  const showCounts = (value: string): void => {
    const chars = textLength(value);
    const lines = value.split('\n').length;
    counter.textContent = fillText(ctx.texts.chars, { n: chars, max: cfg.text.maxChars });
    limits.textContent = '';
    if (chars >= cfg.text.maxChars) limits.append(note(fillText(ctx.texts.tooManyChars, { n: cfg.text.maxChars }), 'warn'));
    if (lines >= cfg.text.maxLines) limits.append(note(fillText(ctx.texts.tooManyLines, { n: cfg.text.maxLines }), 'warn'));
  };
  showCounts(layer.text);

  let typing: number | undefined;
  const commit = (): void => {
    if (typing !== undefined) window.clearTimeout(typing);
    typing = undefined;
    const current = ctx.store.value().layers.find(item => item.id === layer.id);
    if (current?.kind !== 'text') return;
    const lines = area.value.replace(/\r\n?/g, '\n').split('\n').slice(0, cfg.text.maxLines);
    const value = lines.join('\n');
    if (value !== area.value) area.value = value;
    showCounts(value);
    if (current.text === value) return;
    ctx.dispatch({ type: 'update', id: layer.id, patch: { text: value }, tag: `text:${layer.id}` });
  };
  area.addEventListener('input', () => {
    showCounts(area.value);
    if (typing !== undefined) window.clearTimeout(typing);
    typing = window.setTimeout(commit, 120);
  });
  area.addEventListener('focus', () => {
    if (ctx.store.state().selectedId !== layer.id) ctx.dispatch({ type: 'select', id: layer.id });
  });
  area.addEventListener('blur', () => {
    if (typing !== undefined) window.clearTimeout(typing);
    commit();
  });
  cleanups.push(() => {
    if (typing === undefined) return;
    window.clearTimeout(typing);
    typing = undefined;
  });

  box.wrap.append(area, counter, limits);
  return box.wrap;
}

/**
 * "Upiši tekst" — the reference's own add flow: a popover over the canvas with
 * one field, Otkaži and Dodaj, and Dodaj disabled until something is typed.
 */
export function openAddPopover(ctx: DesignerCtx, anchor: HTMLElement): void {
  openPopover({
    ctx,
    anchor,
    title: ctx.texts.textPlaceholder,
    className: 'is-addtext',
    build: (body, handle) => {
      const input = el('input', 'pzd-input');
      input.type = 'text';
      input.placeholder = ctx.texts.textPlaceholder;
      input.maxLength = ctx.config.text.maxChars;
      input.setAttribute('aria-label', ctx.texts.textPlaceholder);
      const row = el('div', 'pzd-over-foot');
      const cancel = textBtn(ctx.texts.cancel, () => handle.close());
      const confirm = el('button', 'pzd-primary', ctx.texts.addText);
      confirm.type = 'button';
      confirm.disabled = true;
      const submit = (): void => {
        const value = input.value.trim();
        if (!value) return;
        addText(ctx, value);
        handle.close();
      };
      input.addEventListener('input', () => {
        confirm.disabled = input.value.trim().length === 0;
      });
      input.addEventListener('keydown', (event) => {
        if ((event as KeyboardEvent).key !== 'Enter') return;
        event.preventDefault();
        submit();
      });
      confirm.addEventListener('click', submit);
      row.append(cancel, confirm);
      body.append(input, row);
    },
  });
}

/** Shared by the popover, the T shortcut and the empty-zone buttons. */
export function addText(ctx: DesignerCtx, text?: string): void {
  const room = canAddLayer(ctx.store.state(), ctx.config, 'text');
  if (!room.ok) {
    ctx.toast(room.reason === 'kind'
      ? ctx.texts.toolNotAllowed
      : fillText(ctx.texts.maxLayers, { n: room.limit ?? ctx.config.maxLayers }), 'error');
    return;
  }
  const fontId = ctx.config.text.defaultFontId
    ?? (ctx.config.text.fontIds.length ? ctx.config.text.fontIds[0]! : undefined)
    ?? ctx.fonts.list[0]?.id
    ?? '';
  void ctx.fonts.ensure(fontId).catch(() => undefined);
  ctx.dispatch({ type: 'add', layer: freshTextLayer(ctx.rawConfig, fontId, text, ctx.addCount()) });
}
